import { Controller } from '@hotwired/stimulus'

// Frena el envío del formulario mientras Cloudflare Turnstile no haya emitido un
// token, y pide un reto nuevo cuando el que había ya no sirve.
//
// Sin esto, tres situaciones acababan en un rechazo del servidor que el usuario no
// entendía (incidencia del 29-jul-2026 en rankia.cl):
//   1. pulsar Enviar antes de que el reto resuelva — habitual en móvil con red lenta
//   2. tardar más de 300 s rellenando el formulario, que es lo que vive el token
//   3. reenviar el formulario con un token ya consumido (son de un solo uso)
//
// NUNCA deja al usuario sin salida, y esto es la parte delicada. Tres cortes:
//
//   a) api.js no llega a cargar (bloqueador) -> `window.turnstile` no existe, pasa
//   b) el widget está roto (`error-callback`) -> pasa, y decide el servidor
//   c) ya hemos avisado una vez -> pasa, para que un reintento nunca se atasque
//
// El caso (b) no es teórico: si Cloudflare se cae, `challenges.cloudflare.com`
// tampoco responde y el widget no emite token nunca. El servidor está preparado
// (fail-open + Sentry, ver `Cloudflare::TurnstileVerificacion`), así que frenar aquí
// anularía ese fail-open y seguiríamos perdiendo inscripciones durante la caída.
// Tampoco tiene sentido pedirle marcar una casilla que no puede aparecer.
//
// Tampoco se deshabilita el botón de enviar, por lo mismo: dejaría al usuario sin
// poder enviar y sin explicación, que es peor que el fallo original.
//
// El controller se engancha al widget, no al <form>, para que el cambio viva en un
// único partial (`ponencias/_turnstile`) en vez de replicarse en los tres
// `_inscribir_gratis` + el FormularioComponent + el captador de blogs.
export default class extends Controller {
  // Espejo de BloqueoDeFormularioVisible::CLASE_CONTENEDOR: los avisos de cliente y
  // de servidor aterrizan en el mismo sitio. Se busca por clase y DENTRO del propio
  // formulario, porque una página puede tener varios contenedores (el captador de
  // blogs se pinta dos veces en móvil) y cada formulario tiene que avisar en el suyo.
  static CLASE_AVISO = 'rnk-AvisoBloqueo'

  static MENSAJE = 'Confirma que no eres un robot para continuar.'

  // Turnstile invoca el `error-callback` por su NOMBRE, buscándolo en `window`, así
  // que no puede ser un método del controlador. El nombre está también en el HAML.
  static CALLBACK_ERROR = 'turnstileWidgetRoto'

  connect () {
    this.publicaCallbackDeError()
    this.formulario = this.element.closest('form')
    if (!this.formulario) return

    // En fase de captura: en el propio <form> los escuchantes de captura van antes que
    // los demás, así que cuando este cancela el envío, los del formulario ya lo ven
    // cancelado. El alta (`registro_controller`) bloquea el botón al enviar, y si
    // corriera antes que este dejaría al usuario sin poder volver a enviar.
    this.alEnviar = this.alEnviar.bind(this)
    this.formulario.addEventListener('submit', this.alEnviar, true)
  }

  disconnect () {
    if (this.formulario) this.formulario.removeEventListener('submit', this.alEnviar, true)
  }

  // Turnstile llama a la global sin decir QUÉ widget falló, y una página puede tener
  // dos (el captador de blogs en móvil), así que la marca se pone en todos. Si
  // Cloudflare no responde, ninguno va a poder emitir token de todas formas.
  //
  // Se publica una sola vez: antes cada `connect()` la sobreescribía con un cierre
  // sobre SU elemento, así que con dos widgets el fallo del primero marcaba el segundo.
  // Al no guardar estado —consulta el DOM en el momento de la llamada— es idempotente,
  // y por eso tampoco se borra en `disconnect`: hacerlo dejaría al otro widget sin
  // callback. La marca sí vive en el elemento, para que Turbo se la lleve al navegar.
  publicaCallbackDeError () {
    if (window[this.constructor.CALLBACK_ERROR]) return

    window[this.constructor.CALLBACK_ERROR] = () => {
      document.querySelectorAll('.cf-turnstile').forEach(widget => {
        widget.dataset.turnstileRoto = 'true'
      })
    }
  }

  alEnviar (evento) {
    if (!window.turnstile) return
    if (this.hayToken()) return
    if (this.widgetRoto()) return
    if (this.yaHemosAvisado) return

    evento.preventDefault()
    this.yaHemosAvisado = true
    this.pideVerificacion()
  }

  widgetRoto () {
    return this.element.dataset.turnstileRoto === 'true'
  }

  hayToken () {
    try {
      const token = window.turnstile.getResponse(this.element)
      return typeof token === 'string' && token.length > 0
    } catch {
      // getResponse lanza si el widget aún no está montado. Sin token que ofrecer,
      // tratarlo como "todavía no verificado" es la lectura correcta.
      return false
    }
  }

  pideVerificacion () {
    // reset() fuerza un reto nuevo, así que sirve igual para el token caducado y
    // para el ya consumido.
    try {
      window.turnstile.reset(this.element)
    } catch {
      // Si el reset falla no hay nada que recuperar por JS; el aviso ya avisa.
    }
    this.muestraAviso()
    this.element.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  muestraAviso () {
    const contenedor = this.formulario.querySelector(`.${this.constructor.CLASE_AVISO}`)
    if (!contenedor) return

    contenedor.innerHTML = ''
    const aviso = document.createElement('div')
    aviso.className = 'rnk-Flash-danger'
    aviso.textContent = this.constructor.MENSAJE
    contenedor.appendChild(aviso)
  }
}
